import React, { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Button, ButtonGroup, Form, Card, Spinner, Container, Alert, Row, Col } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { SketchPicker } from 'react-color';
import MediaSelector from '../Media/MediaSelector';
import { faTrash, faPlus, faImage, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    createCardsSectionInstance,
    createDefaultCard,
    normalizeCardOrders,
    sortCardsByOrder,
    DEFAULT_CARD_STYLE,
    CARD_IMAGE_STYLES,
    CARD_BORDER_RADIUS,
    CARD_TEXT_ALIGN,
    SECTION_CARDS_LAYOUT,
    DEFAULT_SECTION_STYLE,
} from '../../../Models/CardsSection';
import './CardsSectionBuilder.css';

const ColorField = ({ label, color, show, onToggle, onChange }) => (
    <div className="mb-3">
        <label className="form-label">{label}</label>
        <div className="d-flex align-items-center">
            <div
                style={{
                    width: 30,
                    height: 30,
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    marginRight: 10,
                    cursor: 'pointer',
                }}
                onClick={onToggle}
            />
            <span>{color}</span>
        </div>
        {show && (
            <div style={{ position: 'absolute', zIndex: 2 }}>
                <div
                    style={{ position: 'fixed', inset: 0 }}
                    onClick={onToggle}
                />
                <SketchPicker color={color} onChange={onChange} />
            </div>
        )}
    </div>
);

const CardsSectionBuilder = ({ id }) => {
    const [instance] = useState(createCardsSectionInstance());
    const [loading, setLoading] = useState(false);
    const [defaultMessage, setDefaultMessage] = useState(false);

    const [sectionTitle, setSectionTitle] = useState('');
    const [cards, setCards] = useState([]);
    const [currentStyle, setCurrentStyle] = useState({});

    const [showSectionBgPicker, setShowSectionBgPicker] = useState(false);
    const [showSectionTitlePicker, setShowSectionTitlePicker] = useState(false);
    const [showGlobalTitlePicker, setShowGlobalTitlePicker] = useState(false);
    const [showGlobalDescPicker, setShowGlobalDescPicker] = useState(false);
    const [showGlobalCardBgColorPicker, setShowGlobalCardBgColorPicker] = useState(false);
    const [cardColorPickers, setCardColorPickers] = useState({});

    const [sectionBgMediaOpen, setSectionBgMediaOpen] = useState(false);
    const [cardMediaTarget, setCardMediaTarget] = useState(null);
    const [cardBgMediaTarget, setCardBgMediaTarget] = useState(null);

    useEffect(() => {
        getData();
    }, []);

    const syncStyle = (updated) => {
        setCurrentStyle(updated);
        instance.setStyle(updated);
    };

    const getData = async () => {
        try {
            setLoading(true);
            if (id) {
                const data = await instance.load(id);
                if (Object.keys(data).length === 0) setDefaultMessage(true);
            } else {
                await instance.load();
            }
            if (Object.keys(instance.getData()).length > 0) {
                setSectionTitle(instance.getSectionTitle());
                setCards(instance.getCards());
                setCurrentStyle(instance.getStyle());
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load cards section');
        } finally {
            setLoading(false);
        }
    };

    const updateCards = (next) => {
        setCards(next);
        instance.setCards(next);
    };

    const sortedCards = sortCardsByOrder(cards);

    const addCard = () => {
        const nextOrder = sortedCards.length
            ? Math.max(...sortedCards.map((c) => Number(c.order) || 0)) + 1
            : 1;
        updateCards(normalizeCardOrders([...sortedCards, createDefaultCard(Date.now(), nextOrder)]));
    };

    const moveCard = (cardId, direction, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        const sorted = sortCardsByOrder(cards);
        const index = sorted.findIndex((c) => String(c.id) === String(cardId));
        if (index < 0) return;
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= sorted.length) return;
        const next = [...sorted];
        [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
        updateCards(normalizeCardOrders(next));
    };

    const removeCard = (cardId) => {
        if (cards.length <= 1) {
            toast.warning('At least one card is required');
            return;
        }
        updateCards(normalizeCardOrders(cards.filter((c) => c.id !== cardId)));
    };

    const updateCardField = (cardId, field, value) => {
        updateCards(
            cards.map((c) => (c.id === cardId ? { ...c, [field]: value } : c))
        );
    };

    const updateCardStyle = (cardId, styleKey, value) => {
        updateCards(
            cards.map((c) => {
                if (c.id !== cardId) return c;
                return {
                    ...c,
                    style: { ...(c.style || DEFAULT_CARD_STYLE), [styleKey]: value },
                };
            })
        );
    };

    const toggleCardCustomStyle = (cardId, enabled) => {
        updateCards(
            cards.map((c) => {
                if (c.id !== cardId) return c;
                return {
                    ...c,
                    useCustomStyle: enabled,
                    style: enabled
                        ? { ...instance.getGlobalCardStyle(), ...(c.style || {}) }
                        : c.style,
                };
            })
        );
    };

    const handleSectionBgColor = (color) => {
        const updated = {
            ...currentStyle,
            CardsSection: {
                ...currentStyle.CardsSection,
                backgroundColor: color.hex,
            },
        };
        syncStyle(updated);
    };

    const handleSectionTitleColor = (color) => {
        const updated = {
            ...currentStyle,
            CardsSectionTitle: { color: color.hex },
        };
        syncStyle(updated);
    };

    const handleGlobalCardStyle = (key, value) => {
        const updated = {
            ...currentStyle,
            CardsSectionCard: {
                ...currentStyle.CardsSectionCard,
                ...DEFAULT_CARD_STYLE,
                ...currentStyle.CardsSectionCard,
                [key]: value,
            },
        };
        syncStyle(updated);
    };

    const handleGlobalCardColor = (key, color) => {
        handleGlobalCardStyle(key, color.hex);
    };

    const handleSectionBgImage = (path) => {
        const updated = {
            ...currentStyle,
            CardsSection: {
                ...currentStyle.CardsSection,
                backgroundImage: path || '',
            },
        };
        syncStyle(updated);
        setSectionBgMediaOpen(false);
    };

    const handleCardImage = (path) => {
        if (cardMediaTarget) {
            updateCardField(cardMediaTarget, 'imageUrl', path);
        }
        setCardMediaTarget(null);
    };

    const handleCardBgImage = (path) => {
        if (cardBgMediaTarget === 'global') {
            handleGlobalCardBgImage(path);
        } else if (cardBgMediaTarget) {
            updateCardStyle(cardBgMediaTarget, 'backgroundImage', path || '');
        }
        setCardBgMediaTarget(null);
    };

    const handleSectionLayout = (layout) => {
        const updated = {
            ...currentStyle,
            CardsSection: {
                ...DEFAULT_SECTION_STYLE,
                ...currentStyle.CardsSection,
                cardsLayout: layout,
            },
        };
        syncStyle(updated);
    };

    const handleGlobalCardBgImage = (path) => {
        handleGlobalCardStyle('backgroundImage', path || '');
    };

    const toggleCardPicker = (cardId, field) => {
        const key = `${cardId}-${field}`;
        setCardColorPickers((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSaveChanges = async () => {
        try {
            setLoading(true);
            instance.setSectionTitle(sectionTitle);
            instance.setCards(cards);
            await instance.save();
            toast.success('Changes saved successfully!');
            await getData();
        } catch (error) {
            toast.error('Failed to save changes. Please try again.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const globalCard = {
        ...DEFAULT_CARD_STYLE,
        ...currentStyle.CardsSectionCard,
    };

    if (loading && !cards.length) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <div className="container mt-4">
            <ToastContainer />
            {defaultMessage && (
                <Alert variant="info" className="mt-3" dismissible>
                    Default data has been loaded. Happy building!
                </Alert>
            )}
            <h3 className="mb-3">Cards Section Builder</h3>

            <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Content</Accordion.Header>
                    <Accordion.Body>
                        <Form.Group className="mb-4">
                            <Form.Label>Section Title (optional)</Form.Label>
                            <Form.Control
                                type="text"
                                value={sectionTitle}
                                onChange={(e) => setSectionTitle(e.target.value)}
                                placeholder="e.g. Our Programs"
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Cards</h5>
                            <Button variant="outline-primary" size="sm" onClick={addCard}>
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Add Card
                            </Button>
                        </div>

                        <Accordion key={sortedCards.map((c) => `${c.id}-${c.order}`).join('_')}>
                            {sortedCards.map((card, index) => (
                                <Accordion.Item eventKey={`card-${card.id}`} key={`${card.id}-${card.order}`}>
                                    <div className="cards-section-card-header-row">
                                        {sortedCards.length > 1 && (
                                            <div
                                                className="cards-section-card-order-btns"
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                <ButtonGroup size="sm">
                                                    <Button
                                                        type="button"
                                                        variant="outline-secondary"
                                                        disabled={index === 0}
                                                        onClick={(e) => moveCard(card.id, 'up', e)}
                                                        title="Move up"
                                                    >
                                                        <FontAwesomeIcon icon={faArrowUp} />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline-secondary"
                                                        disabled={index === sortedCards.length - 1}
                                                        onClick={(e) => moveCard(card.id, 'down', e)}
                                                        title="Move down"
                                                    >
                                                        <FontAwesomeIcon icon={faArrowDown} />
                                                    </Button>
                                                </ButtonGroup>
                                            </div>
                                        )}
                                        <Accordion.Header className="cards-section-card-accordion-header">
                                            <span className="cards-section-card-header-title">
                                                <span className="text-muted small">#{card.order ?? index + 1}</span>
                                                <span className="text-truncate">
                                                    {card.title?.trim() || `Card ${index + 1}`}
                                                </span>
                                                {card.useCustomStyle && (
                                                    <span className="badge bg-info">Custom style</span>
                                                )}
                                            </span>
                                        </Accordion.Header>
                                    </div>
                                    <Accordion.Body>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Image</Form.Label>
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                {card.imageUrl ? (
                                                    <img
                                                        src={card.imageUrl}
                                                        alt=""
                                                        style={{
                                                            width: 80,
                                                            height: 80,
                                                            objectFit: 'cover',
                                                            borderRadius: card.style?.imageStyle === 'circle' ? '50%' : 8,
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="d-flex align-items-center justify-content-center text-muted"
                                                        style={{
                                                            width: 80,
                                                            height: 80,
                                                            border: '1px dashed #ccc',
                                                            borderRadius: 8,
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faImage} />
                                                    </div>
                                                )}
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => setCardMediaTarget(card.id)}
                                                >
                                                    Select Image
                                                </Button>
                                                {card.imageUrl && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => updateCardField(card.id, 'imageUrl', '')}
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </div>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={card.title}
                                                onChange={(e) => updateCardField(card.id, 'title', e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={card.description}
                                                onChange={(e) => updateCardField(card.id, 'description', e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Check
                                            type="switch"
                                            id={`custom-style-${card.id}`}
                                            className="mb-3"
                                            label="Use custom style for this card"
                                            checked={!!card.useCustomStyle}
                                            onChange={(e) => toggleCardCustomStyle(card.id, e.target.checked)}
                                        />

                                        {card.useCustomStyle && (
                                            <Card className="mb-3 bg-light">
                                                <Card.Body>
                                                    <Row>
                                                        <Col md={6}>
                                                            <ColorField
                                                                label="Title Color"
                                                                color={card.style?.titleColor || globalCard.titleColor}
                                                                show={cardColorPickers[`${card.id}-title`]}
                                                                onToggle={() => toggleCardPicker(card.id, 'title')}
                                                                onChange={(c) => updateCardStyle(card.id, 'titleColor', c.hex)}
                                                            />
                                                        </Col>
                                                        <Col md={6}>
                                                            <ColorField
                                                                label="Description Color"
                                                                color={card.style?.descriptionColor || globalCard.descriptionColor}
                                                                show={cardColorPickers[`${card.id}-desc`]}
                                                                onToggle={() => toggleCardPicker(card.id, 'desc')}
                                                                onChange={(c) => updateCardStyle(card.id, 'descriptionColor', c.hex)}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Image Style</Form.Label>
                                                        <Form.Select
                                                            value={card.style?.imageStyle || 'circle'}
                                                            onChange={(e) => updateCardStyle(card.id, 'imageStyle', e.target.value)}
                                                        >
                                                            <option value="circle">Circle (centered)</option>
                                                            <option value="fill">Fill (top of card)</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Card Border Radius</Form.Label>
                                                        <Form.Select
                                                            value={card.style?.borderRadius || 'rounded'}
                                                            onChange={(e) => updateCardStyle(card.id, 'borderRadius', e.target.value)}
                                                        >
                                                            <option value="rounded">Rounded</option>
                                                            <option value="normal">Normal</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Text Alignment</Form.Label>
                                                        <Form.Select
                                                            value={card.style?.textAlign || 'center'}
                                                            onChange={(e) => updateCardStyle(card.id, 'textAlign', e.target.value)}
                                                        >
                                                            <option value="center">Center</option>
                                                            <option value="justified">Justified</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                    <ColorField
                                                        label="Card Background Color"
                                                        color={card.style?.backgroundColor || globalCard.backgroundColor}
                                                        show={cardColorPickers[`${card.id}-bgColor`]}
                                                        onToggle={() => toggleCardPicker(card.id, 'bgColor')}
                                                        onChange={(c) => updateCardStyle(card.id, 'backgroundColor', c.hex)}
                                                    />
                                                    <Form.Group>
                                                        <Form.Label>Card Background Image (cover)</Form.Label>
                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                            {card.style?.backgroundImage ? (
                                                                <img
                                                                    src={card.style.backgroundImage}
                                                                    alt=""
                                                                    style={{ width: 80, height: 48, objectFit: 'cover', borderRadius: 6 }}
                                                                />
                                                            ) : (
                                                                <span className="text-muted small">None</span>
                                                            )}
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => setCardBgMediaTarget(card.id)}
                                                            >
                                                                Select
                                                            </Button>
                                                            {card.style?.backgroundImage && (
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => updateCardStyle(card.id, 'backgroundImage', '')}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </Form.Group>
                                                </Card.Body>
                                            </Card>
                                        )}

                                        <div className="d-flex justify-content-end">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => removeCard(card.id)}
                                                disabled={cards.length <= 1}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="me-1" />
                                                Remove Card
                                            </Button>
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>Section Style</Accordion.Header>
                    <Accordion.Body>
                        <ColorField
                            label="Background Color"
                            color={currentStyle.CardsSection?.backgroundColor || '#ffffff'}
                            show={showSectionBgPicker}
                            onToggle={() => setShowSectionBgPicker(!showSectionBgPicker)}
                            onChange={handleSectionBgColor}
                        />
                        <ColorField
                            label="Section Title Color"
                            color={currentStyle.CardsSectionTitle?.color || '#212529'}
                            show={showSectionTitlePicker}
                            onToggle={() => setShowSectionTitlePicker(!showSectionTitlePicker)}
                            onChange={handleSectionTitleColor}
                        />
                        <Form.Group className="mb-3">
                            <Form.Label>Cards Layout</Form.Label>
                            <Form.Select
                                value={currentStyle.CardsSection?.cardsLayout || 'normal'}
                                onChange={(e) => handleSectionLayout(e.target.value)}
                            >
                                {SECTION_CARDS_LAYOUT.map((layout) => (
                                    <option key={layout} value={layout}>
                                        {layout === 'center' ? 'Center (cluster cards)' : 'Normal (full width grid)'}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Section Background Image (cover)</Form.Label>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                {currentStyle.CardsSection?.backgroundImage ? (
                                    <img
                                        src={currentStyle.CardsSection.backgroundImage}
                                        alt=""
                                        style={{ width: 120, height: 72, objectFit: 'cover', borderRadius: 8 }}
                                    />
                                ) : (
                                    <span className="text-muted small">No background image</span>
                                )}
                                <Button variant="outline-primary" size="sm" onClick={() => setSectionBgMediaOpen(true)}>
                                    Select Image
                                </Button>
                                {currentStyle.CardsSection?.backgroundImage && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleSectionBgImage('')}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </div>
                        </Form.Group>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                    <Accordion.Header>Global Card Style (all cards)</Accordion.Header>
                    <Accordion.Body>
                        <p className="text-muted small">
                            Default styles for every card. Enable &quot;custom style&quot; on a card to override these for that card only.
                        </p>
                        <Row>
                            <Col md={6}>
                                <ColorField
                                    label="Title Color"
                                    color={globalCard.titleColor}
                                    show={showGlobalTitlePicker}
                                    onToggle={() => setShowGlobalTitlePicker(!showGlobalTitlePicker)}
                                    onChange={(c) => handleGlobalCardColor('titleColor', c)}
                                />
                            </Col>
                            <Col md={6}>
                                <ColorField
                                    label="Description Color"
                                    color={globalCard.descriptionColor}
                                    show={showGlobalDescPicker}
                                    onToggle={() => setShowGlobalDescPicker(!showGlobalDescPicker)}
                                    onChange={(c) => handleGlobalCardColor('descriptionColor', c)}
                                />
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Image Style</Form.Label>
                            <Form.Select
                                value={globalCard.imageStyle}
                                onChange={(e) => handleGlobalCardStyle('imageStyle', e.target.value)}
                            >
                                {CARD_IMAGE_STYLES.map((s) => (
                                    <option key={s} value={s}>
                                        {s === 'circle' ? 'Circle (centered)' : 'Fill (top of card)'}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Card Border Radius</Form.Label>
                            <Form.Select
                                value={globalCard.borderRadius}
                                onChange={(e) => handleGlobalCardStyle('borderRadius', e.target.value)}
                            >
                                {CARD_BORDER_RADIUS.map((r) => (
                                    <option key={r} value={r}>
                                        {r === 'rounded' ? 'Rounded' : 'Normal'}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Text Alignment</Form.Label>
                            <Form.Select
                                value={globalCard.textAlign}
                                onChange={(e) => handleGlobalCardStyle('textAlign', e.target.value)}
                            >
                                {CARD_TEXT_ALIGN.map((align) => (
                                    <option key={align} value={align}>
                                        {align === 'center' ? 'Center' : 'Justified'}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <ColorField
                            label="Card Background Color"
                            color={globalCard.backgroundColor}
                            show={showGlobalCardBgColorPicker}
                            onToggle={() => setShowGlobalCardBgColorPicker(!showGlobalCardBgColorPicker)}
                            onChange={(c) => handleGlobalCardColor('backgroundColor', c)}
                        />
                        <Form.Group>
                            <Form.Label>Card Background Image (cover)</Form.Label>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                {globalCard.backgroundImage ? (
                                    <img
                                        src={globalCard.backgroundImage}
                                        alt=""
                                        style={{ width: 120, height: 72, objectFit: 'cover', borderRadius: 8 }}
                                    />
                                ) : (
                                    <span className="text-muted small">No card background image</span>
                                )}
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setCardBgMediaTarget('global')}
                                >
                                    Select Image
                                </Button>
                                {globalCard.backgroundImage && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleGlobalCardBgImage('')}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </div>
                        </Form.Group>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <div className="d-flex justify-content-end mt-3">
                <Button variant="primary" onClick={handleSaveChanges} disabled={loading}>
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </div>

            <MediaSelector
                isOpen={sectionBgMediaOpen}
                onClose={() => setSectionBgMediaOpen(false)}
                onSelect={handleSectionBgImage}
                isMultiple={false}
            />
            <MediaSelector
                isOpen={!!cardMediaTarget}
                onClose={() => setCardMediaTarget(null)}
                onSelect={handleCardImage}
                isMultiple={false}
            />
            <MediaSelector
                isOpen={!!cardBgMediaTarget}
                onClose={() => setCardBgMediaTarget(null)}
                onSelect={handleCardBgImage}
                isMultiple={false}
            />
        </div>
    );
};

export default CardsSectionBuilder;
